import React, { useState } from "react";
import { X } from "lucide-react";

export interface TagsInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  variant?: "skills" | "exclusions";
  disabled?: boolean;
}

export function TagsInput({
  tags,
  onChange,
  placeholder = "Введите тег и нажмите Enter",
  variant = "skills",
  disabled,
}: TagsInputProps): React.ReactElement {
  const [inputValue, setInputValue] = useState("");

  const isMaxReached = tags.length >= 20;

  const addTag = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (tags.length >= 20) {
      setInputValue("");
      return;
    }
    if (!tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputValue);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  const removeTag = (indexToRemove: number) => {
    onChange(tags.filter((_, i) => i !== indexToRemove));
  };

  const isExclusion = variant === "exclusions";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* Input */}
      <input
        data-testid={`tags-input-${variant}`}
        type="text"
        maxLength={64}
        value={inputValue}
        placeholder={isMaxReached ? "Максимум 20" : placeholder}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled || isMaxReached}
        style={{
          width: "100%",
          padding: "12px 16px",
          backgroundColor: "var(--color-canvas)",
          border: "1px solid var(--color-frost-border)",
          borderRadius: "var(--radius-input)",
          fontSize: "14px",
          color: "var(--color-graphite)",
          outline: "none",
        }}
      />

      {/* Tags List */}
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {tags.map((tag, idx) => (
            <span
              key={`${tag}-${idx}`}
              data-testid={`tag-pill-${tag}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 500,
                padding: "5px 10px",
                borderRadius: "var(--radius-pill)",
                backgroundColor: isExclusion
                  ? "var(--color-destructive-bg)"
                  : "var(--color-success-bg)",
                color: isExclusion ? "var(--color-destructive)" : "var(--color-success)",
                border: isExclusion
                  ? "1px solid rgba(189, 49, 71, 0.2)"
                  : "1px solid rgba(23, 115, 77, 0.2)",
              }}
            >
              {isExclusion ? `- ${tag}` : `+ ${tag}`}
              {!disabled && (
                <button
                  type="button"
                  data-testid={`remove-tag-btn-${tag}`}
                  onClick={() => removeTag(idx)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: "inherit",
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
